<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { ArrowLeft, DocumentCopy, Download } from '@element-plus/icons-vue'

const route = useRoute()
const router = useRouter()
const examId = computed(() => route.params.id)
const loading = ref(false)
const meta = ref(null)
const yamlText = ref('')

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
  'Content-Type': 'application/json'
})

const fetchYaml = async () => {
  loading.value = true
  try {
    const { data } = await axios.post(
      '/api/v1/admin/exam/export-yaml',
      { id: Number(examId.value) },
      { headers: authHeaders() }
    )
    if (data.code === 200 && data.data) {
      meta.value = data.data
      yamlText.value = data.data.yaml || ''
    } else {
      ElMessage.error(data.message || '导出失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('导出 YAML 失败')
  } finally {
    loading.value = false
  }
}

const copyYaml = async () => {
  if (!yamlText.value) return
  try {
    await navigator.clipboard.writeText(yamlText.value)
    ElMessage.success('已复制到剪贴板')
  } catch {
    ElMessage.error('复制失败，请手动选择文本复制')
  }
}

const downloadYaml = () => {
  if (!yamlText.value) return
  const name = meta.value?.examSname || `exam-${examId.value}`
  const blob = new Blob([yamlText.value], { type: 'text/yaml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${name}.yaml`
  a.click()
  URL.revokeObjectURL(url)
}

const goBack = () => router.push(`/admin/exam/${examId.value}`)

onMounted(fetchYaml)
</script>

<template>
  <div class="admin-page exam-yaml-export" v-loading="loading">
    <div class="page-toolbar">
      <el-button :icon="ArrowLeft" @click="goBack">返回测评详情</el-button>
      <div class="toolbar-actions">
        <el-button :icon="DocumentCopy" :disabled="!yamlText" @click="copyYaml">复制 YAML</el-button>
        <el-button type="primary" :icon="Download" :disabled="!yamlText" @click="downloadYaml">
          下载 .yaml
        </el-button>
        <el-button @click="fetchYaml">重新加载</el-button>
      </div>
    </div>

    <el-card v-if="meta" class="meta-card" shadow="never">
      <template #header>
        <span>测评 YAML 导出</span>
      </template>
      <p><strong>ID：</strong>{{ meta.examId }}</p>
      <p><strong>短名：</strong>{{ meta.examSname }}</p>
      <p><strong>名称：</strong>{{ meta.examName }}</p>
      <p class="hint">
        包含 exams 表全部字段；question_keys、original_questions、feedbacks、summarys、stops 等 JSON
        已解析为 YAML 嵌套结构，便于备份与编辑。
      </p>
    </el-card>

    <el-input
      v-model="yamlText"
      type="textarea"
      :rows="28"
      readonly
      class="yaml-editor"
      placeholder="加载中…"
    />
  </div>
</template>

<style scoped>
.exam-yaml-export {
  padding: 0 4px;
}

.page-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-card {
  margin-bottom: 16px;
}

.meta-card p {
  margin: 0 0 8px;
  font-size: 14px;
}

.hint {
  color: var(--el-text-color-secondary);
  margin-top: 8px !important;
}

.yaml-editor :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
}
</style>
